/**
 * Firebase & Third-party Error Suppressor
 *
 * Questo modulo DEVE essere importato come primo import in index.js
 * per intercettare gli errori Firebase "app-offline" e gli errori
 * di terze parti (Iubenda, Stripe, ecc.) prima che
 * React Error Overlay li mostri.
 *
 * L'errore "installations/app-offline" è comune in sviluppo locale
 * quando Firebase Installations non riesce a connettersi ai server.
 */

(function setupFirebaseErrorSuppressor() {
  if (typeof window === 'undefined') return;

  // Helper per verificare se un errore deve essere soppresso
  const shouldSuppressError = (error, message) => {
    const errorMessage = message || error?.message || '';
    const errorCode = error?.code || '';
    
    // Script error cross-origin
    if (errorMessage === 'Script error.' || errorMessage === 'Script error') {
      return true;
    }
    
    // Firebase offline errors
    if (errorCode === 'installations/app-offline' ||
        errorMessage.includes('app-offline') ||
        errorMessage.includes('Application offline') ||
        errorMessage.includes('installations/app-offline')) {
      return true;
    }
    
    // Iubenda errors
    const stack = error?.stack || '';
    if (stack.includes('iubenda') || stack.includes('autoblocking') ||
        errorMessage.includes("reading 'cs'") || errorMessage.includes("reading 'cmp'")) {
      return true;
    }
    
    return false;
  };

  // CRITICAL: Intercetta React Error Overlay patchando addEventListener
  // Questo deve essere fatto PRIMA che React Error Overlay si registri
  if (process.env.NODE_ENV === 'development') {
    const originalAddEventListener = window.addEventListener;
    // Flag per prevenire ricorsione
    let isHandlingError = false;

    window.addEventListener = function(type, listener, options) {
      if (type === 'error') {
        // Wrappa il listener per filtrare errori suppressibili
        const wrappedListener = function(event) {
          // Previeni ricorsione
          if (isHandlingError) return;

          if (shouldSuppressError(event.error, event.message)) {
            console.debug('[ErrorSuppressor] Error event suppressed:', event.message);
            event.preventDefault();
            event.stopImmediatePropagation();
            return;
          }

          // Proteggi la chiamata al listener originale
          try {
            isHandlingError = true;
            return listener.apply(this, arguments);
          } catch (listenerError) {
            // Se il listener stesso causa un errore, non propagarlo
            console.debug('[ErrorSuppressor] Listener threw error, suppressing:', listenerError.message);
            return;
          } finally {
            isHandlingError = false;
          }
        };
        return originalAddEventListener.call(this, type, wrappedListener, options);
      }
      if (type === 'unhandledrejection') {
        // Wrappa anche unhandledrejection per errori async Firebase
        const wrappedListener = function(event) {
          // Previeni ricorsione
          if (isHandlingError) return;

          const error = event.reason;
          if (shouldSuppressError(error, error?.message)) {
            console.debug('[ErrorSuppressor] Async error suppressed:', error?.message);
            event.preventDefault();
            event.stopImmediatePropagation();
            return;
          }

          // Proteggi la chiamata al listener originale
          try {
            isHandlingError = true;
            return listener.apply(this, arguments);
          } catch (listenerError) {
            console.debug('[ErrorSuppressor] Rejection listener threw error, suppressing:', listenerError.message);
            return;
          } finally {
            isHandlingError = false;
          }
        };
        return originalAddEventListener.call(this, type, wrappedListener, options);
      }
      return originalAddEventListener.call(this, type, listener, options);
    };
  }

  // Intercetta errori sincroni
  const originalOnerror = window.onerror;
  window.onerror = function(message, source, lineno, colno, error) {
    if (shouldSuppressError(error, message)) {
      if (process.env.NODE_ENV === 'development') {
        console.debug('[ErrorSuppressor] Window.onerror suppressed:', message);
      }
      return true; // Sopprimi l'errore
    }
    if (originalOnerror) {
      return originalOnerror.apply(this, arguments);
    }
    return false;
  };

  // Intercetta promise rejections - usa capture phase per eseguire prima di altri handler
  window.addEventListener('unhandledrejection', function(event) {
    const error = event.reason;
    if (shouldSuppressError(error, error?.message)) {
      if (process.env.NODE_ENV === 'development') {
        console.debug('[ErrorSuppressor] Unhandled rejection suppressed:', error?.message);
      }
      event.preventDefault();
      event.stopImmediatePropagation();
      return;
    }
  }, true);

  // Intercetta anche errori standard con capture phase per eseguire prima di Sentry
  window.addEventListener('error', function(event) {
    const error = event.error;
    const message = event.message || '';
    
    if (shouldSuppressError(error, message)) {
      if (process.env.NODE_ENV === 'development') {
        console.debug('[ErrorSuppressor] Error event suppressed:', message);
      }
      event.preventDefault();
      event.stopImmediatePropagation();
      return true;
    }
  }, true);

  // Intercetta console.error per filtrare errori di terze parti in development
  // Questo cattura errori loggati da Sentry prima che i nostri handler li intercettino
  if (process.env.NODE_ENV === 'development') {
    const originalConsoleError = console.error;
    console.error = function(...args) {
      // Controlla se il primo argomento contiene un messaggio di errore suppressibile
      const firstArg = args[0];
      if (firstArg) {
        const argString = typeof firstArg === 'string' ? firstArg : (firstArg.message || String(firstArg));

        // Supprime errori Iubenda
        if (argString.includes("reading 'cs'") ||
            argString.includes("reading 'cmp'") ||
            argString.includes('iubenda') ||
            argString.includes('autoblocking')) {
          console.debug('[ErrorSuppressor] console.error suppressed:', argString.substring(0, 100));
          return;
        }

        // Supprime "Script error" generici
        if (argString === 'Script error.' || argString === 'Script error') {
          return;
        }
      }

      // Per TypeError, controlla anche il messaggio
      if (firstArg instanceof TypeError) {
        const msg = firstArg.message || '';
        if (msg.includes("reading 'cs'") || msg.includes("reading 'cmp'")) {
          console.debug('[ErrorSuppressor] TypeError suppressed:', msg);
          return;
        }
      }

      return originalConsoleError.apply(console, args);
    };
  }

  // Log solo in development
  if (process.env.NODE_ENV === 'development') {
    console.debug('[FirebaseErrorSuppressor] Handler installati');
  }
})();
